// @ts-nocheck
import {
  CreateSearchIntentResponse,
  CreateSearchIntentFlightProps,
  CreateSearchIntentResult,
} from "shared/services/flights/createSearchIntentResponse";
import {
  SERPCardResult,
  SERPFilter,
  SERPFlight,
  SERPFlightSector,
  SERPPagination,
  SERPResult,
  SERPSellerResult,
} from "../../../shared/types/serpResult";

const checkExistingFlightResult = (
  serpCardResults: SERPCardResult[],
  apiResult: CreateSearchIntentResult,
): number | null => {
  if (serpCardResults === null || serpCardResults === undefined) {
    return null;
  }

  if (serpCardResults.length === 0) {
    return null;
  }

  for (let index = 0; index < serpCardResults.length - 1; index++) {
    const cardResult = serpCardResults[index];

    // Se existe um voo de ida
    const firstSector = cardResult.sectors.find(
      (s) =>
        s.index === 0 &&
        s.airline === apiResult.outboundFlights.flight.airline &&
        s.arrivalAirportIata ===
          apiResult.outboundFlights.flight.destinationAirport &&
        s.arrivalDate ===
          parseDate(apiResult.outboundFlights.flight.arrivalDate) &&
        s.arrivalTime ===
          parseTime(apiResult.outboundFlights.flight.arrivalTime) &&
        s.departureAirportIata ===
          apiResult.outboundFlights.flight.originAirport &&
        s.departureDate ===
          parseDate(apiResult.outboundFlights.flight.departureDate) &&
        s.departureTime ===
          parseTime(apiResult.outboundFlights.flight.departureTime) &&
        s.flights.length === apiResult.outboundFlights.flightsDetails.length,
    );

    // Se existe um voo de volta
    const secondSector = cardResult.sectors.find((s) =>
      s.index === 1 && apiResult.returnFlights
        ? s.airline === apiResult.returnFlights?.flight.airline &&
          s.arrivalAirportIata ===
            apiResult.returnFlights?.flight.destinationAirport &&
          s.arrivalDate ===
            parseDate(apiResult.returnFlights?.flight.arrivalDate) &&
          s.arrivalTime ===
            parseTime(apiResult.returnFlights?.flight.arrivalTime) &&
          s.departureAirportIata ===
            apiResult.returnFlights?.flight.originAirport &&
          s.departureDate ===
            parseDate(apiResult.returnFlights?.flight.departureDate) &&
          s.departureTime ===
            parseTime(apiResult.returnFlights?.flight.departureTime) &&
          s.flights.length === apiResult.returnFlights?.flightsDetails.length
        : false,
    );

    if (firstSector !== undefined && secondSector !== undefined) {
      return index;
    }
  }

  return null;
};

export const orderResultsByPrice = (serpCardResults: SERPCardResult[]) => {
  if (serpCardResults && serpCardResults.length > 0) {
    for (let index = 0; index < serpCardResults?.length - 1; index++) {
      const card = serpCardResults[index];
      card.sellers.sort(
        (a: SERPSellerResult, b: SERPSellerResult) => a.price - b.price,
      );
    }
    return serpCardResults.sort(
      (a: SERPCardResult, b: SERPCardResult) => a.minPrice - b.minPrice,
    );
  }
};

type ScoreEntry = NonNullable<
  CreateSearchIntentResponse["message"]["score"]
>[number];

const getSellerScoreKey = (seller: SERPSellerResult) => {
  const name =
    seller.integrationName.toLowerCase() === "skyscanner"
      ? `${seller.integrationName}_${seller.sellerId}`
      : seller.sellerId;

  return name.toLowerCase();
};

export const applyScoresToResults = (
  serpCardResults: SERPCardResult[],
  scoreEntries: ScoreEntry[] = [],
) => {
  const valuesByName = new Map(
    scoreEntries.map(({ name, value }) => [name.toLowerCase(), value]),
  );

  return serpCardResults.map((card) => ({
    ...card,
    sellers: card.sellers.map((seller) => {
      const scoredSeller = { ...seller };
      const value = valuesByName.get(getSellerScoreKey(seller));

      if (
        typeof value === "number" &&
        Number.isFinite(value) &&
        Number.isFinite(seller.price) &&
        seller.price > 0
      ) {
        scoredSeller.score = value / seller.price;
      } else {
        delete scoredSeller.score;
      }

      return scoredSeller;
    }),
  }));
};

const getCardBestOption = (card: SERPCardResult) =>
  card.sellers.reduce<SERPSellerResult | undefined>((bestSeller, seller) => {
    if (typeof seller.score !== "number" || !Number.isFinite(seller.score)) {
      return bestSeller;
    }

    if (
      !bestSeller ||
      seller.score > bestSeller.score! ||
      (seller.score === bestSeller.score && seller.price < bestSeller.price)
    ) {
      return seller;
    }

    return bestSeller;
  }, undefined);

export const orderResultsByBestOption = (serpCardResults: SERPCardResult[]) =>
  serpCardResults
    .map((result, index) => ({
      result,
      index,
      bestOption: getCardBestOption(result),
    }))
    .sort((a, b) => {
      if (a.bestOption && b.bestOption) {
        return (
          b.bestOption.score! - a.bestOption.score! ||
          a.bestOption.price - b.bestOption.price ||
          a.index - b.index
        );
      }

      if (a.bestOption) return -1;
      if (b.bestOption) return 1;

      return a.result.minPrice - b.result.minPrice || a.index - b.index;
    })
    .map(({ result }) => result);

const parseFlights = (
  flightsDetails: CreateSearchIntentFlightProps[] | null,
): SERPFlight[] => {
  const serpFlights: SERPFlight[] = [];

  if (flightsDetails !== null) {
    flightsDetails.forEach((flight, index) => {
      serpFlights.push({
        index,
        airline: flight.airline,
        departureDate: parseDate(flight.departureDate),
        departureTime: parseTime(flight.departureTime),
        departureTimeDifferentDay: null,
        departureAirportIata: flight.originAirport,
        departureDayOfWeek: parseDayOfWeek(flight.departureDate),
        totalDuration: flight.durationFlight,
        arrivalDate: parseDate(flight.arrivalDate),
        arrivalTime: parseTime(flight.arrivalTime),
        arrivalTimeDifferentDay: parseDayOfWeek(flight.arrivalDate),
        arrivalAirportIata: flight.destinationAirport,
        arrivalDayOfWeek: parseDayOfWeek(flight.arrivalDate),
        flightNumber: flight.flightNumber,
        airlineLogo: "",
      });
    });
  }

  return serpFlights;
};

const parseDayOfWeek = (apiDate: string): number => {
  return parseDate(apiDate).getDay();
};

const parseTime = (apiTime: string): Date => {
  const [hours, minutes] = apiTime.split(":").map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error("Formato inválido. Use HH:mm.");
  }

  const now = new Date(); // Pega a data atual
  now.setHours(hours, minutes, 0, 0); // Define apenas o horário

  return now;
};

const parseDate = (apiDate: string): Date => {
  const [day, month, year] = apiDate.split("/").map(Number);

  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    throw new Error("Formato inválido. Use D/M/YYYY.");
  }

  return new Date(year, month - 1, day);
};

const parseSector = (
  index: number,
  flight: CreateSearchIntentFlightProps | null,
  flightsDetails: CreateSearchIntentFlightProps[] | null,
): SERPFlightSector | null => {
  const serpFlightSector = {} as SERPFlightSector;

  if (flight !== null && flightsDetails !== null) {
    serpFlightSector.index = index;
    serpFlightSector.airline = flight.airline;
    serpFlightSector.airlineLogo = `https://voopter.com.br/voopter-static-assets/airline-logos/${flight.airline}.png`;
    serpFlightSector.departureDate = parseDate(flight.departureDate);
    serpFlightSector.departureTime = parseTime(flight.departureTime);
    serpFlightSector.departureTimeDifferentDay = null;
    serpFlightSector.departureAirportIata = flight.originAirport;
    serpFlightSector.departureDayOfWeek = parseDayOfWeek(flight.departureDate);
    serpFlightSector.totalDuration = flight.durationFlight;
    serpFlightSector.arrivalDate = parseDate(flight.arrivalDate);
    serpFlightSector.arrivalTime = parseTime(flight.arrivalTime);
    serpFlightSector.arrivalTimeDifferentDay = null;
    serpFlightSector.arrivalAirportIata = flight.destinationAirport;
    serpFlightSector.arrivalDayOfWeek = parseDayOfWeek(flight.arrivalDate);
    serpFlightSector.flights = parseFlights(flightsDetails);
    serpFlightSector.stops = serpFlightSector.flights.length - 1;
  } else {
    return null;
  }

  return serpFlightSector;
};

const parsePriceRange = (results: SERPCardResult[]) => {
  if (results === undefined || results?.length === 0)
    return { minPrice: 0, maxPrice: 0 };

  // const prices = results.flatMap((card) =>
  //   card.sellers.map((seller) => seller.price),
  // );
  const prices = results.flatMap((card) => card.sellers[0].price);

  return {
    minPrice: Math.min(...prices),
    maxPrice: Math.max(...prices),
  };
};

export const parseAirportsFilters = (
  serpCardResults: SERPCardResult[],
  type: "origin" | "destination",
) => {
  const airportFrequency: Record<string, number> = {};

  for (let index = 0; index < serpCardResults?.length; index++) {
    const result = serpCardResults[index];

    if (result.sectors && result.sectors[0]) {
      const flight = result.sectors[0];
      airportFrequency[
        type === "origin"
          ? flight.departureAirportIata
          : flight.arrivalAirportIata
      ] =
        (airportFrequency[
          type === "origin"
            ? flight.departureAirportIata
            : flight.arrivalAirportIata
        ] || 0) + 1;
    }
  }

  return Object.entries(airportFrequency)
    .sort((a, b) => b[1] - a[1])
    .map(([airport]) => airport);
};

export const parseAirlinesFilters = (serpCardResults: SERPCardResult[]) => {
  const airlinesFrequency: Record<string, number> = {};

  for (let index = 0; index < serpCardResults?.length; index++) {
    const result = serpCardResults[index];

    for (let sectorCount = 0; sectorCount < 2; sectorCount++) {
      if (result.sectors && result.sectors[sectorCount]) {
        const sector = result.sectors[sectorCount];

        // Frequencia do Primeiro Setor
        airlinesFrequency[sector.airline] =
          (airlinesFrequency[sector.airline] || 0) + 1;

        for (let fIndex = 0; fIndex < sector.flights.length; fIndex++) {
          const flight = sector.flights[fIndex];

          // Frequencia do Primeiro Setor
          airlinesFrequency[flight.airline] =
            (airlinesFrequency[flight.airline] || 0) + 1;
        }
      }
    }
  }

  return Object.entries(airlinesFrequency)
    .sort((a, b) => b[1] - a[1])
    .map(([airport]) => airport);
};

export const parseSellersFilters = (serpCardResults: SERPCardResult[]) => {
  const sellersFrequency: Record<string, number> = {};

  for (let index = 0; index < serpCardResults?.length; index++) {
    const result = serpCardResults[index];

    for (let sIndex = 0; sIndex < result.sellers.length; sIndex++) {
      const seller = result.sellers[sIndex];

      sellersFrequency[seller.sellerId] =
        (sellersFrequency[seller.sellerId] || 0) + 1;
    }
  }

  return Object.entries(sellersFrequency)
    .sort((a, b) => b[1] - a[1])
    .map(([airport]) => airport);
};

export const getSellerImgById = (
  serpCardResults: SERPCardResult[],
  targetSellerId: string,
): string | null => {
  if (serpCardResults) {
    for (const result of serpCardResults) {
      for (const seller of result.sellers) {
        if (seller.sellerId === targetSellerId) {
          return seller.sellerImg;
        }
      }
    }
  }
  return null;
};

/** Compara só HH:mm de um Date ou string parseável por Date */
const extractHourMinute = (d?: Date | string): string | undefined => {
  if (!d) return undefined;
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return undefined;
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

/** Compara apenas Y-M-D (sem timezone), usando campos locais do Date */
const sameYMD = (a?: Date, b?: Date): boolean => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const isSameItinerary = (a: SERPCardResult, b: SERPCardResult): boolean => {
  if (!a || !b || a.sectors.length !== b.sectors.length) return false;

  for (let i = 0; i < a.sectors.length; i++) {
    const A = a.sectors[i];
    const B = b.sectors[i];

    const same =
      // aeroportos (usar os campos que você realmente preenche em parseSector)
      A.departureAirportIata === B.departureAirportIata &&
      A.arrivalAirportIata === B.arrivalAirportIata &&
      // *** NOVO: MESMO DIA DE PARTIDA ***
      sameYMD(A.departureDate, B.departureDate) &&
      // horários (HH:mm)
      extractHourMinute(A.departureTime) ===
        extractHourMinute(B.departureTime) &&
      extractHourMinute(A.arrivalTime) === extractHourMinute(B.arrivalTime);

    if (!same) return false;
  }

  return true;
};

/**
 * Junta ofertas novas de um poll às que o card já tem, uma por vendedor.
 *
 * - Vendedor que ainda não foi tratado neste poll: a oferta nova substitui a
 *   anterior (de um poll antigo), com preço e link. O parceiro pode ter
 *   atualizado a tarifa; ficar com a mais barata de todos os polls mostrava um
 *   preço que não existia mais.
 * - Vendedor já tratado neste poll (mesmo voo, mais de uma tarifa, como a
 *   MaxMilhas): fica a de menor preço.
 *
 * `updatedSellerIds` guarda os vendedores já tratados neste poll para este card
 * e é alterado aqui.
 */
const mergeSellersFromPoll = (
  currentSellers: SERPSellerResult[],
  incomingSellers: SERPSellerResult[],
  updatedSellerIds: Set<string>,
): SERPSellerResult[] => {
  const bySellerId = new Map<string, SERPSellerResult>(
    currentSellers.map((seller) => [seller.sellerId, seller]),
  );

  for (const seller of incomingSellers) {
    const current = bySellerId.get(seller.sellerId);
    const isCheaperInSamePoll =
      updatedSellerIds.has(seller.sellerId) &&
      current !== undefined &&
      current.price <= seller.price;

    if (!isCheaperInSamePoll) {
      bySellerId.set(seller.sellerId, seller);
    }

    updatedSellerIds.add(seller.sellerId);
  }

  return Array.from(bySellerId.values());
};

export const parseCreateSearchIntentToSERPResult = async (
  apiResults: CreateSearchIntentResponse,
  currentSERPResult: SERPResult | null,
): SERPResult => {
  const emptyResults: SERPResult = {
    results: [],
    total: 0,
    pagination: {
      total: 0,
      totalPages: 0,
      currentPage: 0,
      maxItemsPerPage: 0,
    },
    predefinedFilters: undefined,
    appliedFilters: null,
  };

  const serpResult =
    currentSERPResult !== null ? currentSERPResult : ({} as SERPResult);

  // Vendedores já atualizados neste poll, por card (ver mergeSellersFromPoll).
  const updatedSellersByCard = new Map<SERPCardResult, Set<string>>();

  if (
    apiResults?.message?.wsChannels &&
    apiResults?.message?.wsChannels.length > 0
  ) {
    // Para cada trecho único (ROTA + DATA)
    for (const wsChannel of apiResults.message.wsChannels) {
      // Para cada Vendedor
      Object.values(wsChannel.cacheData).forEach((seller) => {
        if (
          seller.status === "finished" ||
          (seller.data && seller.data.length > 0)
        ) {
          for (const sellerResults of seller.data) {
            // Card que vamos tentar encaixar ou criar
            const cardResult: SERPCardResult = {
              sectors: [],
              minPrice: 0,
              sellers: [],
            };

            // Tarifas
            const sellers: SERPSellerResult[] = [];
            for (const pricing of sellerResults.pricingOptions) {
              sellers.push({
                integrationName: pricing.integrationName,
                sellerId: pricing.agent.name,
                price: parseInt(
                  pricing.price.substring(0, pricing.price.length - 1),
                ),
                sellerName: pricing.agent.name,
                sellerImg: pricing.agent.logo,
                deepLink: pricing.redirectDetails.webDeeplink,
                uniqueID: sellerResults.uniqueID,
              });
            }

            // Adiciona setores (ida/volta)
            let firstSector = parseSector(
              0,
              sellerResults.outboundFlights.flight,
              sellerResults.outboundFlights.flightsDetails,
            );
            if (firstSector) {
              cardResult.sectors.push(firstSector);
            }

            if (
              sellerResults.returnFlights !== undefined &&
              sellerResults.returnFlights !== null
            ) {
              let secondSector = parseSector(
                1,
                sellerResults.returnFlights?.flight,
                sellerResults.returnFlights?.flightsDetails,
              );
              if (secondSector) {
                cardResult.sectors.push(secondSector);
              }
            }

            let existingFlightIndex: number | null = null;
            for (let i = 0; i < (serpResult.results?.length ?? 0); i++) {
              if (isSameItinerary(serpResult.results[i], cardResult)) {
                existingFlightIndex = i;
                break;
              }
            }

            if (existingFlightIndex != null) {
              const existingCard = serpResult.results[existingFlightIndex];
              let updatedSellerIds = updatedSellersByCard.get(existingCard);
              if (!updatedSellerIds) {
                updatedSellerIds = new Set<string>();
                updatedSellersByCard.set(existingCard, updatedSellerIds);
              }

              existingCard.sellers = mergeSellersFromPoll(
                existingCard.sellers,
                sellers,
                updatedSellerIds,
              );
              existingCard.minPrice = Math.min(
                ...existingCard.sellers.map((s) => s.price),
              );
            } else {
              const updatedSellerIds = new Set<string>();
              cardResult.sellers = mergeSellersFromPoll(
                [],
                sellers,
                updatedSellerIds,
              );
              updatedSellersByCard.set(cardResult, updatedSellerIds);
              cardResult.minPrice = Math.min(
                ...cardResult.sellers.map((s) => s.price),
              );
              if (!serpResult.results) serpResult.results = [];
              serpResult.results.push(cardResult);
            }
          }
        }
      });
    }
  } else {
    return emptyResults;
  }

  serpResult.results = applyScoresToResults(
    serpResult.results ?? [],
    apiResults.message.score,
  );

  // Atualiza os números gerais
  if (!serpResult.pagination) {
    serpResult.pagination = {} as SERPPagination;
  }
  serpResult.pagination.currentPage = 1;
  serpResult.pagination.maxItemsPerPage = 10;
  serpResult.total = serpResult?.results?.length ?? 0;
  serpResult.pagination.total = serpResult?.results?.length ?? 0;
  serpResult.pagination.totalPages = Math.ceil(
    serpResult.total / serpResult.pagination.maxItemsPerPage,
  );

  // Atualiza os Filtros Pré Definidos
  serpResult.predefinedFilters = {} as SERPFilter;

  // Conexões
  serpResult.predefinedFilters.stops = [0, 1, 2, 3];

  // Preços
  const priceRange = parsePriceRange(serpResult.results);
  serpResult.predefinedFilters.priceFrom = priceRange.minPrice;
  serpResult.predefinedFilters.priceTo = priceRange.maxPrice;

  // Aeroportos de Origem
  const originAirports = parseAirportsFilters(serpResult.results, "origin");
  serpResult.predefinedFilters.airportsFrom = originAirports;

  // Aeroportos de Destino
  const returnAirports = parseAirportsFilters(
    serpResult.results,
    "destination",
  );
  serpResult.predefinedFilters.airportsTo = returnAirports;

  // Cia Aéreas
  const airlines = parseAirlinesFilters(serpResult.results);
  serpResult.predefinedFilters.airlines = airlines;

  // Sellers
  const sellers = parseSellersFilters(serpResult.results);
  serpResult.predefinedFilters.sellers = sellers;

  // Cópia dos Pré Definidos para Applied
  serpResult.appliedFilters = { ...serpResult.predefinedFilters };

  // Ordena por preço
  serpResult.results =
    orderResultsByPrice(serpResult.results) ?? serpResult.results;

  // Atualiza o minPrice de cada resultado
  if (serpResult.results && serpResult.results.length > 0) {
    serpResult.results.forEach((result) => {
      result.minPrice = Math.min(...result.sellers.map((s) => s.price));
    });
  }

  // Ordena novamente por segurança
  serpResult.results =
    orderResultsByPrice(serpResult.results) ?? serpResult.results;

  return serpResult;
};
